import React, { useEffect, useMemo, useState } from "react";
// ANT DESIGN COMPONENTS
import { Drawer, Tabs, Modal } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
  updateOpenModalControlPanelBank,
  updateLiftSelected,
  updateBankSelected,
  updateChannelId,
  updateServerId,
} from "../../../redux/monitoring/monitoringSlice";
import { useLazyGetServerQuery } from "../../../redux/servers/serverAPI";
// CUSTOM HOOKS
import { useLastBankMessage } from "../socketMessageState/BankMessagesContext";
// COMPONENTS
import ButtonClose from "../../components/buttons/ButtonClose";
import ControlPanelBankHeader from "./ControlPanelBankHeader";
import ControlPanelBankVIP from "./ControlPanelBankVIP";
import ControlPanelBankHallCalls from "./ControlPanelBankHallCalls";
import ControlPanelBankFaults from "./ControlPanelBankFaults";
import ControlPanelBankConfiguration from "./ControlPanelBankConfiguration";
// INTERFACES
import { BankFloorInterface } from "../../../interfaces/Bank.interface";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { RootState } from "../../../app/store";
import { ServerInterface } from "../../../interfaces/Server.interface";
import { DeviceServiceContent } from "../../../flatbuffers/generated/service/device-service-content";
import { ElevatorService } from "../../../flatbuffers/generated/service/elevator-service";
// AUTHORIZATION
import validatePermission from "../../../utils/validatePermissions";
import GETJwtToken from "../../../redux/authentication/authentication";
// CONSTANTS
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
import { BLANK_LIFT } from "../../../constants/lifts";
import { BLANK_BANK } from "../../../constants/bank";
import { BLANK_SERVER } from "../../../constants/server";
// UTILS
import { stringToUuidT, EMPTY_UUID } from "../../../utils/uuid";

function ControlPanelBank() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");
  const dispatch = useDispatch();

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [tabActiveKey, setTabActiveKey] = useState<string>("HALL_CALLS");
  const [displayFrontDoors, setDisplayFrontDoors] = useState<boolean>(true);
  const [displayRearDoors, setDisplayRearDoors] = useState<boolean>(true);
  const [bankFloors, setBankFloors] = useState<BankFloorInterface[]>([]);
  const [liftVip, setLiftVip] = useState<any>({});
  const [socketMessageVip, setSocketMessageVip] =
    useState<SocketMessageInterface>({
      ...BLANK_SOCKET_MESSAGE,
    });
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [vipServiceModeStopped, setVipServiceModeStopped] =
    useState<boolean>(false);
  const [server, setServer] = useState<ServerInterface>({ ...BLANK_SERVER });

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const {
    openModalControlPanelBank,
    controlPanelDrawerPlacement,
    bankSelected,
    serverId,
  } = useSelector((state: RootState) => state.monitoring);
  const { permissions } = useSelector((state: RootState) => state.user);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [triggerGetServer] = useLazyGetServerQuery();

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const bankUuid = useMemo(
    () => (bankSelected.id ? stringToUuidT(bankSelected.id) : EMPTY_UUID),
    [bankSelected.id]
  );
  const socketMessageBank: SocketMessageInterface =
    useLastBankMessage(bankUuid) || BLANK_SOCKET_MESSAGE;

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const isVipMode = (msg: SocketMessageInterface): boolean =>
    msg.serviceMode?.messageType ===
    DeviceServiceContent.ElevatorServiceMessage &&
    msg.serviceMode?.message?.message === ElevatorService.VIP;

  const getServer = async () => {
    const token = await GETJwtToken();
    try {
      const result = await triggerGetServer({
        token,
        idServer: serverId,
      }).unwrap();
      if (result && result.data) {
        setServer(result.data);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const clearVipMode = () => {
    setLiftVip({});
    setSocketMessageVip({
      ...BLANK_SOCKET_MESSAGE,
    });
  };

  const onClickClose = () => {
    if (liftVip.id && socketMessageVip.bankId) {
      setOpenModal(true);
    } else {
      dispatch(updateOpenModalControlPanelBank(false));
      dispatch(updateLiftSelected({ ...BLANK_LIFT }));
      dispatch(updateBankSelected({ ...BLANK_BANK }));
      dispatch(updateChannelId(""));
      dispatch(updateServerId(""));
    }
  };

  const onClickCancel = () => {
    setOpenModal(false);
  };

  const generateFrontRearConfiguration = () => {
    let bankHasFrontDoors = false;
    let bankHasRearDoors = false;
    const newBankFloors: BankFloorInterface[] = [];

    bankSelected.bank_floors.map((floor: any) => {
      // Check if has front or rear doors
      if (floor.front_door) {
        bankHasFrontDoors = true;
      }
      if (floor.rear_door) {
        bankHasRearDoors = true;
      }
      newBankFloors.push({ ...floor });
      return true;
    });
    setBankFloors(newBankFloors);
    setDisplayFrontDoors(bankHasFrontDoors);
    setDisplayRearDoors(bankHasRearDoors);
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    if (isVipMode(socketMessageBank)) {
      setSocketMessageVip(socketMessageBank);
      const index = bankSelected.lifts.findIndex(
        (object: any) => object.id === socketMessageBank.liftId
      );
      if (index >= 0) {
        setLiftVip(bankSelected.lifts[index]);
      }
    }
    if (
      liftVip &&
      liftVip.id &&
      socketMessageBank.liftId === liftVip.id &&
      !isVipMode(socketMessageBank)
    ) {
      setVipServiceModeStopped(true);
      setTimeout(() => {
        setVipServiceModeStopped(false);
      }, 100);
    }
  }, [socketMessageBank]);

  useEffect(() => {
    if (bankSelected.bank_floors.length > 0 && bankSelected.lifts.length > 0) {
      generateFrontRearConfiguration();
    }
    if (bankSelected.type === "ELEVATOR") {
      setTabActiveKey("HALL_CALLS");
    } else {
      setTabActiveKey("FAULTS");
    }
  }, [bankSelected]);

  useEffect(() => {
    if (openModalControlPanelBank) {
      setLiftVip({});
    }
  }, [openModalControlPanelBank]);

  useEffect(() => {
    if (serverId !== "") {
      getServer();
    }
  }, [serverId]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <Drawer
      width="800"
      placement={controlPanelDrawerPlacement}
      open={openModalControlPanelBank}
      closable={false}
      onClose={onClickClose}
      footer={
        <div style={{ textAlign: "right" }}>
          <ButtonClose onClick={onClickClose} />
        </div>
      }
    >
      <div>
        <ControlPanelBankHeader liftVip={liftVip} server={server} />
      </div>
      <br />
      <div>
        <Tabs
          onTabClick={(key: string) => {
            setTabActiveKey(key);
          }}
          activeKey={tabActiveKey}
        >
          {validatePermission(
            "monitor-control-panel-hall-calls",
            permissions
          ) &&
            bankSelected.type === "ELEVATOR" && (
              <Tabs.TabPane key="HALL_CALLS" tab="Hall Calls">
                <ControlPanelBankHallCalls
                  hasFrontDoors={displayFrontDoors}
                  hasRearDoors={displayRearDoors}
                  bankFloors={bankFloors}
                  frontLock={[
                    ...socketMessageBank.hallCalls.up.frontLock,
                    ...socketMessageBank.hallCalls.down.frontLock,
                  ]}
                  rearLock={[
                    ...socketMessageBank.hallCalls.upAssigned.rearLock,
                    ...socketMessageBank.hallCalls.downAssigned.rearLock,
                  ]}
                />
              </Tabs.TabPane>
            )}
          {validatePermission("monitor-control-panel-vip-mode", permissions) &&
            bankSelected.type === "ELEVATOR" && (
              <Tabs.TabPane key="VIP" tab="VIP Mode">
                <ControlPanelBankVIP
                  hasFrontDoors={displayFrontDoors}
                  hasRearDoors={displayRearDoors}
                  bankFloors={bankFloors}
                  liftVip={liftVip}
                  socketMessage={socketMessageVip}
                  clearVipMode={clearVipMode}
                  onClickClose={onClickClose}
                  vipServiceModeStopped={vipServiceModeStopped}
                  server={server}
                />
              </Tabs.TabPane>
            )}
          {validatePermission("monitor-control-panel-faults", permissions) && (
            <Tabs.TabPane key="FAULTS" tab="Faults">
              <ControlPanelBankFaults />
            </Tabs.TabPane>
          )}
          {validatePermission(
            "monitor-control-panel-bank-configuration",
            permissions
          ) && (
              <Tabs.TabPane key="CONFIGURATION" tab="Configuration">
                <ControlPanelBankConfiguration bankId={bankSelected.id} />
              </Tabs.TabPane>
            )}
        </Tabs>
      </div>
      <Modal
        title={t("general.warningCloseVipModal")}
        open={openModal}
        onOk={onClickClose}
        onCancel={onClickCancel}
      >
        <p>{t("general.warningCloseVipModalDescription")}</p>
      </Modal>
    </Drawer>
  );
}

export default ControlPanelBank;
